import {
  BeamShapingGroupDto,
  instanceOfBeamShapingGroupCircularDto,
  instanceOfBeamShapingGroupRectangularDto,
  instanceOfMvTherapySystemSetupDto,
  MedicalSystemSetupBaseDto,
  TaskTypeDescription,
} from '@/api/generator';
import { isNumber } from 'lodash';

/**
 * Task types whose medical system setup lets the user add catalog setup parameters
 * on top of the template. BCC ships a fixed, read-only template.
 */
export function allowsCustomSetupParameters(taskType?: TaskTypeDescription | null): boolean {
  return taskType === 'ManualTask' || taskType === 'BeamAnalysis';
}

/**
 * Whether a single setup parameter may be removed from the medical system setup.
 *
 * The template marks its default parameters with `isRequired`; those stay. Manual tasks
 * have no template defaults, so everything the user enabled can be removed again.
 */
export function isSetupParameterRemovable(
  taskType: TaskTypeDescription | null | undefined,
  parameter: { isRequired?: boolean | null },
): boolean {
  if (!allowsCustomSetupParameters(taskType)) {
    return false;
  }

  return taskType === 'ManualTask' || !parameter.isRequired;
}

/**
 * Task types whose target area holds exactly one QA equipment group, with no group name.
 *
 * BCC has always worked this way. A beam analysis measures with one detector interface and
 * the one array connected to it, so its area gets one group as well. Manual tasks let the
 * user build any number of named groups.
 */
export function usesSingleQaEquipmentGroup(taskType?: TaskTypeDescription | null): boolean {
  return taskType === 'BeamConstancyCheck' || taskType === 'BeamAnalysis';
}

/**
 * Task types that carry task type specific data on each target area
 * (`TaskItemAreaDto.taskTypeData`), served as a template by
 * `getTaskItemAreaDataTemplate`. Beam analysis stores its corrections there.
 */
export function hasTaskItemAreaData(taskType?: TaskTypeDescription | null): boolean {
  return taskType === 'BeamAnalysis';
}

/**
 * A beam shaping the backend can work with: its shape is set and its field sizes are
 * filled in. Field edges derive from the sizes on the client, so they are not checked.
 */
export function isBeamShapingComplete(group: BeamShapingGroupDto): boolean {
  if (!isNumber(group.radiationFieldShape?.value?.id)) {
    return false;
  }

  if (instanceOfBeamShapingGroupRectangularDto(group)) {
    return isNumber(group.radiationFieldSizeX?.value) && isNumber(group.radiationFieldSizeY?.value);
  }

  if (instanceOfBeamShapingGroupCircularDto(group)) {
    return isNumber(group.radiationFieldSize?.value);
  }

  return false;
}

/**
 * Whether the setup combinations can be requested as far as the beam shapings are concerned.
 *
 * The backend rejects an enabled beam shaping container without a complete beam shaping.
 * On a beam analysis task the template enables the container but ships it empty; the first
 * (incomplete) row is added by the form afterwards, so a request fired on medical system
 * selection would be rejected. Setups without a beam shaping container are always ready.
 */
export function areBeamShapingsReadyForCombinations(
  setup: MedicalSystemSetupBaseDto | null | undefined,
): boolean {
  if (!setup || !instanceOfMvTherapySystemSetupDto(setup)) {
    return true;
  }

  const container = setup.beamShapingContainer;

  if (!container?.isEnabled) {
    return true;
  }

  return (container.selectedBeamShapings ?? []).some(isBeamShapingComplete);
}

/**
 * Task types whose evaluations are user-defined (manual task model): one scope per area,
 * the user picks quantities and enumerations from the data type catalog of the medical
 * system. Beam analysis reuses this model on the backend; its template provider ships no
 * evaluation scope templates.
 */
export function usesManualEvaluations(taskType?: TaskTypeDescription | null): boolean {
  return taskType === 'ManualTask' || taskType === 'BeamAnalysis';
}
